import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
// Store
import { IRootState } from '../../../redux';
// Assets
import styles from './menubardropdownlist.module.scss';
// Interfaces
import { folderActions, IFolder, virtualFoldersEnum } from '../../../interfaces/folder.interfaces';
// Enums
import {
    OrganizationCategoryAction,
    FilterAction,
    MenuBarButtonType,
    MessageState,
    MoreActionsItems,
    MoveToFolderItems,
    PermissionType,
    SelectAllItems,
    SortAction,
    SortFilterType,
    SupportedMimeType,
} from '../../../enums';
// Config
import { ConfigContext } from '../../../providers';
import { staticConfig } from '../../../config/static';
// Helpers
import { isMediaSizeLessThanMD } from '../../../helpers/layout';
import { isTouchScreen } from '../../../helpers/browser';
import { getGlobalCssVariables, getSelectedDocuments, hexToRgb } from '../../../helpers/general';
import { isPrintable } from '../../../helpers/print';
import { isDraftFolder } from '../../../helpers/folder';
import { isAllowed } from '../../../helpers/access';
import { cn } from '../../../helpers';
import { Image, ScrollView } from '../..';
import AcceptIcon from '../../../assets/images/svg/accepticon';
import images from '../../../assets/images/images';

interface IMenuBarDropdownListProps {
    show: boolean;
    type: MenuBarButtonType;
    onItemSelected: (type: MenuBarButtonType, action: string, actionValue: string) => void;
    rightAligned?: boolean;
    keyEvent?: React.KeyboardEvent;
    onClickedOutside?: (target: Element) => void;
    onMouseEnter?: () => void;
    customList?: Array<ISelectionAction>;
}

export interface ISelectionAction {
    translateKey: string;
    enabled: boolean;
    visible: boolean;
    selectionValue: string;
    showValue: boolean;
    valueElements: JSX.Element[];
    actionValue?: string;
    desc?: string;
}

interface IMenuBarDropdownListState {
    showList: boolean;
    selectedIndex: number;
    customFolders: IFolder[];
    showClosingAnim: boolean;
}

export enum staticItems {
    SEPARATOR = '9990',
    STATIC_TEXT = '9991',
}

class MenuBarDropdownList extends React.Component<ReduxType, IMenuBarDropdownListState> {
    private dropDownListRef = React.createRef<HTMLDivElement>();

    selectionActions: Array<ISelectionAction> = [
        {
            translateKey: 'CHECK_ALL',
            enabled: true,
            visible: true,
            selectionValue: SelectAllItems.CHECK_ALL,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'CHECK_UNREAD',
            enabled: true,
            visible: true,
            selectionValue: SelectAllItems.CHECK_UNREAD,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'CHECK_READ',
            enabled: true,
            visible: true,
            selectionValue: SelectAllItems.CHECK_READ,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'UNCHECK_ALL',
            enabled: true,
            visible: true,
            selectionValue: SelectAllItems.UNCHECK_ALL,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
    ];

    moveToFolderActions: Array<ISelectionAction> = [
        {
            translateKey: 'MOVE_MAILS_TO_FOLDER',
            enabled: false,
            visible: true,
            selectionValue: MoveToFolderItems.STATIC,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: '',
            enabled: true,
            visible: true,
            selectionValue: MoveToFolderItems.CUSTOM_FOLDERS,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'MOVE_MAILS_TO_OTHER_FOLDER',
            enabled: true,
            visible: true,
            selectionValue: MoveToFolderItems.MOVE_MAILS_TO_OTHER_FOLDER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'MOVE_MAILS_TO_NEW_FOLDER',
            enabled: true,
            visible: true,
            selectionValue: MoveToFolderItems.MOVE_MAILS_TO_NEW_FOLDER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
    ];

    moreActions: Array<ISelectionAction> = [
        {
            translateKey: 'VIEW_CORRESPONDENCE',
            enabled: false,
            visible: true,
            selectionValue: MoreActionsItems.VIEW_CORRESPONDENCE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        // Hide "download" and "information" on documents
        // https://jira.e-boks.dk/browse/FREM-1603
        // Re-enable as part of https://jira.e-boks.dk/browse/FREM-693
        // {
        //     translateKey: 'DOWNLOAD',
        //     enabled: true,
        //     visible: true,
        //     selectionValue: moreActionsItems.DOWNLOAD,
        //     showValue: false,
        //     valueElements: [],
        //     actionValue: null,
        // },
        {
            translateKey: 'DELETE',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.DELETE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'TEXT_PRINT',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.TEXT_PRINT,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'MARK_AS_READ',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.MARK_AS_READ,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'MARK_AS_UNREAD',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.MARK_AS_UNREAD,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ADD_FLAG',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.ADD_FLAG,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'REMOVE_FLAG',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.REMOVE_FLAG,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'RENAME_MAILS',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.RENAME_MAILS,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'FORWARD',
            enabled: true,
            visible: false,
            selectionValue: MoreActionsItems.FORWARD,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ARCHIVE',
            enabled: true,
            visible: false,
            selectionValue: MoreActionsItems.ARCHIVE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ADD_NOTE',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.ADD_NOTE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'EDIT_NOTE',
            enabled: true,
            visible: true,
            selectionValue: MoreActionsItems.EDIT_NOTE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        //   Hide "download" and "information" on documents
        //   https://jira.e-boks.dk/browse/FREM-1603
        // {
        //     translateKey: 'INFORMATION',
        //     enabled: true,
        //     visible: true,
        //     selectionValue: moreActionsItems.INFORMATION,
        //     showValue: false,
        //     valueElements: [],
        //     actionValue: null,
        // },
    ];

    folderMenuActions: Array<ISelectionAction> = [
        {
            translateKey: 'MOVE_FOLDER',
            enabled: true,
            visible: true,
            selectionValue: folderActions.MOVE_FOLDER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'DELETE_FOLDER',
            enabled: true,
            visible: true,
            selectionValue: folderActions.DELETE_FOLDER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'RENAME_FOLDER',
            enabled: true,
            visible: true,
            selectionValue: folderActions.RENAME_FOLDER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
    ];

    sortFilterActions: Array<ISelectionAction> = [
        {
            translateKey: 'SORT_BY',
            enabled: false,
            visible: true,
            selectionValue: staticItems.STATIC_TEXT,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'SORT_BY_DATE_NEWEST_TOP_REDESIGN',
            enabled: true,
            visible: true,
            selectionValue: SortAction.SORT_BY_DATE_NEWEST_TOP,
            showValue: false,
            valueElements: [],
            actionValue: SortFilterType.SORT,
        },
        {
            translateKey: 'SORT_BY_DATE_NEWEST_BOTTOM_REDESIGN',
            enabled: true,
            visible: true,
            selectionValue: SortAction.SORT_BY_DATE_NEWEST_BOTTOM,
            showValue: false,
            valueElements: [],
            actionValue: SortFilterType.SORT,
        },
        {
            translateKey: 'SORT_BY_SENDER_ATO_Z_REDESIGN',
            enabled: true,
            visible: true,
            selectionValue: SortAction.SORT_BY_SENDER_ATO_Z,
            showValue: false,
            valueElements: [],
            actionValue: SortFilterType.SORT,
        },
        {
            translateKey: 'SORT_BY_TITLE_ATO_Z',
            enabled: true,
            visible: true,
            selectionValue: SortAction.SORT_BY_TITLE_ATO_Z,
            showValue: false,
            valueElements: [],
            actionValue: SortFilterType.SORT,
        },
        {
            translateKey: '',
            enabled: false,
            visible: true,
            selectionValue: staticItems.SEPARATOR,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'FILTER_BY',
            enabled: false,
            visible: true,
            selectionValue: staticItems.STATIC_TEXT,
            showValue: true,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'FILTER_BY_UNREAD',
            enabled: true,
            visible: true,
            selectionValue: FilterAction.FILTER_BY_UNREAD,
            showValue: false,
            valueElements: [],
            actionValue: SortFilterType.FILTER,
        },
    ];

    organizationCategoryActions: Array<ISelectionAction> = [
        {
            translateKey: 'ORGANIZATION_CATEGORY_STATE',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_STATE,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_REGION',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_REGION,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_MUNICIPALITY',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_MUNICIPALITY,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_OTHER',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_OTHER,
            showValue: false,
            valueElements: [],
            actionValue: null,
        },
    ];

    constructor(props: ReduxType) {
        super(props);

        this.state = {
            showList: false,
            selectedIndex: -1,
            customFolders: [],
            showClosingAnim: false,
        };

        MenuBarDropdownList.contextType = ConfigContext;
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleOutsideClick);
        document.addEventListener('touchstart', this.handleOutsideClick);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutsideClick);
        document.removeEventListener('touchstart', this.handleOutsideClick);
    }

    handleOutsideClick = (event: MouseEvent | TouchEvent) => {
        const node = event.target as Element;

        if (!this.dropDownListRef.current || !this.dropDownListRef.current.contains(node)) {
            this.setState({ showList: false, selectedIndex: -1 });

            if (this.props.onClickedOutside) {
                this.props.onClickedOutside(node);
            }
        }
    };

    componentDidUpdate(prevProps: ReduxType) {
        if (prevProps.orientation !== this.props.orientation) {
            this.setState({ showList: false });
            return;
        }

        if (prevProps.show !== this.props.show) {
            if (!this.props.show) {
                this.setState({ showClosingAnim: true });
                setTimeout(() => {
                    this.setState({ showClosingAnim: false });
                }, 400);
            }
            if (this.props.type === MenuBarButtonType.MOVE_TO_FOLDER) {
                const rootFolder = this.props.folders.filter((value: IFolder) => {
                    return value.parentId === null;
                });
                const customFolders = this.props.folders.filter((value: IFolder) => {
                    return (
                        value.folderType === 'Custom' &&
                        (value.parentId === rootFolder[0].id || value.parentId === virtualFoldersEnum.MORE_FOLDERS) &&
                        value.id !== this.props.selectedFolder.id
                    );
                });
                this.setState({ customFolders, showList: this.props.show, selectedIndex: -1 });
            } else {
                this.setState({ showList: this.props.show, selectedIndex: -1 });
            }
        }

        if (prevProps.keyEvent !== this.props.keyEvent) {
            const event = this.props.keyEvent;
            switch (event.keyCode || event.charCode) {
                case 13: // Enter
                    this.onEnterAndSpaceKeyHandler();
                    break;

                case 27: // Escape
                    this.setState({ showList: false });
                    break;

                case 32: // Space
                    this.onEnterAndSpaceKeyHandler();
                    break;

                case 38: // Up
                    this.upKeyHandler();
                    break;

                case 40: // Down
                    this.downKeyHandler();
                    break;

                default:
                    break;
            }
        }
    }

    getListByType = () => {
        switch (this.props.type) {
            case MenuBarButtonType.SELECT_ALL:
                return this.selectionActions;

            case MenuBarButtonType.MOVE_TO_FOLDER:
                return this.moveToFolderActions;

            case MenuBarButtonType.MORE_ACTIONS:
                return this.moreActions;

            case MenuBarButtonType.FOLDER_ACTION:
                return this.folderMenuActions;

            case MenuBarButtonType.SORT_FILTER:
                return this.sortFilterActions;

            case MenuBarButtonType.CUSTOM_LIST:
                return this.props.customList;

            case MenuBarButtonType.ORGANIZATION_CATEGORY:
                return this.organizationCategoryActions;
        }

        return null;
    };

    setElementActionByType = (actions: Array<ISelectionAction>) => {
        switch (this.props.type) {
            case MenuBarButtonType.SELECT_ALL:
                this.setSelectAllValueElements(actions);
                break;

            case MenuBarButtonType.MOVE_TO_FOLDER:
                this.setMoveToFolderValueElements(actions);
                break;

            case MenuBarButtonType.MORE_ACTIONS:
                this.setMoreMenuItemsConfiguration(actions);
                break;

            case MenuBarButtonType.FOLDER_ACTION:
                this.setFolderActionsValueElements(actions);
                break;
        }
    };

    onEnterAndSpaceKeyHandler = () => {
        if (this.state.showList) {
            const actionList = this.getListByType().filter((action) => action.visible);
            this.setElementActionByType(actionList);
            const actionIndex = this.handleEnterOnCustomFolderMenuItem(actionList);
            if (actionIndex === -1) return;

            if (actionList[actionIndex].selectionValue in staticItems || !actionList[actionIndex].enabled) {
                return;
            }

            this.handleSelectionAction(
                actionList[actionIndex].selectionValue,
                String(actionList[actionIndex].actionValue)
            );
        }
    };

    handleEnterOnCustomFolderMenuItem = (actionList: ISelectionAction[]): number => {
        let actionIndex = this.state.selectedIndex;
        if (this.props.type === MenuBarButtonType.MOVE_TO_FOLDER) {
            const customFolderIndex = actionList.findIndex(
                (action) => action.selectionValue === MoveToFolderItems.CUSTOM_FOLDERS
            );
            const customFoldersCount = this.state.customFolders.length;
            if (customFoldersCount > 0) {
                const folderItem = document.getElementById('folderMoveItemId' + this.state.selectedIndex);
                if (folderItem) {
                    if (customFolderIndex >= 0) {
                        const folderIndex = this.state.selectedIndex - (customFolderIndex + 1);
                        this.handleSelectionAction(
                            MoveToFolderItems.CUSTOM_FOLDERS,
                            this.state.customFolders[folderIndex].id
                        );
                        return -1;
                    }
                }
            }
            if (customFolderIndex === this.state.selectedIndex) return -1;
            if (customFolderIndex < this.state.selectedIndex) {
                actionIndex -= customFoldersCount;
            }
        }

        return actionIndex;
    };

    upKeyHandler = () => {
        let selectedIndex = this.state.selectedIndex - 1;
        if (selectedIndex < 0) return;
        const visibleActions = this.getListByType().filter((action) => action.visible);
        while (visibleActions[selectedIndex] && !visibleActions[selectedIndex].enabled && selectedIndex > 0) {
            selectedIndex--;
        }
        if (visibleActions[selectedIndex] && !visibleActions[selectedIndex].enabled) return;
        this.setState({ selectedIndex });
        this.scrollFolderItemIntoView(visibleActions, selectedIndex);
    };

    downKeyHandler = () => {
        let selectedIndex = this.state.selectedIndex + 1;
        const visibleActions = this.getListByType().filter((action) => action.visible);
        const maxItemCount = visibleActions.length - 1 + this.state.customFolders.length;
        if (selectedIndex > maxItemCount) return;
        while (
            visibleActions[selectedIndex] &&
            !visibleActions[selectedIndex].enabled &&
            selectedIndex <= maxItemCount
        ) {
            selectedIndex++;
        }
        if (
            visibleActions[selectedIndex] &&
            this.props.type === MenuBarButtonType.MOVE_TO_FOLDER &&
            visibleActions[selectedIndex].selectionValue === MoveToFolderItems.CUSTOM_FOLDERS
        )
            selectedIndex++;
        this.setState({ selectedIndex });
        this.scrollFolderItemIntoView(visibleActions, selectedIndex);
    };

    scrollFolderItemIntoView = (visibleActions: ISelectionAction[], selectedIndex: number) => {
        if (!visibleActions[selectedIndex]) {
            const listItemFolder = document.getElementById('folderMoveItemId' + selectedIndex);
            if (listItemFolder) {
                listItemFolder.scrollIntoView(false);
            }
        }
    };

    onMouseEnterHandler = (index: number) => {
        if (index !== this.state.selectedIndex) {
            this.setState({ selectedIndex: index });
        }
        if (this.props.onMouseEnter) {
            this.props.onMouseEnter();
        }
    };

    onMouseLeaveHandler = () => {
        this.setState({ selectedIndex: -1 });
    };

    setSelectAllValueElements = (actions: Array<ISelectionAction>) => {
        const documents = this.props.isSenderClicked
            ? this.props.selectSenderFolder.originalRecords
            : this.props.documentList;
        actions.forEach((action) => {
            action.valueElements = [];
            switch (action.selectionValue) {
                case SelectAllItems.CHECK_ALL:
                    action.valueElements.push(
                        <React.Fragment key={'valueElement' + action.selectionValue}>
                            {`(${documents.length})`}
                        </React.Fragment>
                    );
                    break;

                case SelectAllItems.CHECK_UNREAD:
                    const unreadDocuments = documents.filter((document) => !document.read);
                    action.valueElements.push(
                        <React.Fragment key={'valueElement' + action.selectionValue}>
                            {`(${unreadDocuments.length})`}
                        </React.Fragment>
                    );
                    break;

                case SelectAllItems.CHECK_READ:
                    const readDocuments = documents.filter((document) => document.read);
                    action.valueElements.push(
                        <React.Fragment key={'valueElement' + action.selectionValue}>
                            {`(${readDocuments.length})`}
                        </React.Fragment>
                    );
                    break;

                case SelectAllItems.UNCHECK_ALL:
                    break;
            }
        });
    };

    setMoveToFolderValueElements = (actions: Array<ISelectionAction>) => {
        actions.forEach((action) => {
            action.valueElements = [];
            switch (action.selectionValue) {
                case staticItems.STATIC_TEXT:
                    action.valueElements.push(
                        <React.Fragment key={'valueElement' + action.selectionValue}>:</React.Fragment>
                    );
                    break;

                case MoveToFolderItems.CUSTOM_FOLDERS:
                case MoveToFolderItems.MOVE_MAILS_TO_NEW_FOLDER:
                    if (!this.context.features.folder.customFolders) {
                        action.visible = false;
                    }
                    break;
            }
        });
    };

    setMoreMenuItemsConfiguration = (actions: Array<ISelectionAction>) => {
        const oneSelected = this.props.selectedDocuments.length === 1;
        const isLessThanMDAndPreview = isMediaSizeLessThanMD(this.props.mediaSize) && !!this.props.previewDocument;
        const selDocumentList = getSelectedDocuments(
            this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
            this.props.selectedDocuments
        );
        const isDraft =
            selDocumentList.length > 0 && selDocumentList.some((doc) => doc.messageState === MessageState.DRAFT);
        const isSent =
            selDocumentList.length > 0 && selDocumentList.some((doc) => doc.messageState === MessageState.SENT);
        actions.forEach((action) => {
            switch (action.selectionValue) {
                case MoreActionsItems.VIEW_CORRESPONDENCE:
                    action.visible = !isLessThanMDAndPreview;
                    break;

                case MoreActionsItems.DELETE:
                    action.visible = this.context.features.message.delete && isLessThanMDAndPreview;
                    break;

                case MoreActionsItems.TEXT_PRINT:
                    action.visible =
                        this.context.features.message.print &&
                        this.props.previewDocument &&
                        isPrintable(this.props.activeArifact.files[0].encodingFormat as SupportedMimeType);
                    break;

                case MoreActionsItems.MARK_AS_READ:
                    if (isAllowed(this.props.access, PermissionType.SETMESSAGEREAD)) {
                        action.visible = !isLessThanMDAndPreview && this.isSelectedDocumentsUnRead();
                    } else {
                        action.visible = false;
                    }

                    action.enabled =
                        !isDraftFolder(this.props.selectedFolder.id, this.props.folders) &&
                        !this.props.readUnreadButtonDisabled;
                    break;

                case MoreActionsItems.MARK_AS_UNREAD:
                    if (isAllowed(this.props.access, PermissionType.SETMESSAGEREAD)) {
                        action.visible = !isLessThanMDAndPreview && this.isSelectedDocumentsRead();
                    } else {
                        action.visible = false;
                    }

                    action.enabled =
                        !isDraftFolder(this.props.selectedFolder.id, this.props.folders) &&
                        !this.props.readUnreadButtonDisabled;
                    break;

                case MoreActionsItems.ADD_FLAG:
                    action.visible = !isLessThanMDAndPreview && this.isSelectedDocumentsAddFlag();

                    action.enabled =
                        !isDraftFolder(this.props.selectedFolder.id, this.props.folders) &&
                        !this.props.addRemoveFlagButtonDisabled;
                    break;

                case MoreActionsItems.REMOVE_FLAG:
                    action.visible = !isLessThanMDAndPreview && this.isSelectedDocumentsRemoveFlag();

                    action.enabled =
                        !isDraftFolder(this.props.selectedFolder.id, this.props.folders) &&
                        !this.props.addRemoveFlagButtonDisabled;
                    break;

                case MoreActionsItems.RENAME_MAILS:
                    action.visible = !isLessThanMDAndPreview && this.context.features.message.rename;
                    break;

                case MoreActionsItems.FORWARD:
                    action.visible =
                        isMediaSizeLessThanMD(this.props.mediaSize) && this.context.features.message.forward;
                    action.enabled = !isDraft && !isSent;
                    break;

                case MoreActionsItems.ARCHIVE:
                    action.visible = !isLessThanMDAndPreview && this.context.features.message.archive;
                    break;

                case MoreActionsItems.ADD_NOTE:
                    action.visible = false;
                    if (this.context.features.message.note.add && !isLessThanMDAndPreview) {
                        if (this.props.previewDocument) {
                            action.visible = !this.props.previewDocument.note;
                        } else {
                            action.visible = !this.isSelectedDocumentNoteAdded();
                        }
                    }
                    action.enabled = oneSelected;
                    break;

                case MoreActionsItems.EDIT_NOTE:
                    action.visible = false;
                    if (this.context.features.message.note.add && !isLessThanMDAndPreview) {
                        if (this.props.previewDocument) {
                            action.visible = !!this.props.previewDocument.note;
                        } else {
                            action.visible = this.isSelectedDocumentNoteAdded();
                        }
                    }
                    action.enabled = oneSelected;
                    break;

                //   Hide "download" and "information" on documents
                //   https://jira.e-boks.dk/browse/FREM-1603

                // case moreActionsItems.DOWNLOAD:
                //     action.visible = !isLessThanMDAndPreview && this.context.features.message.save;
                //     break;

                // case moreActionsItems.INFORMATION:
                //     action.visible = !isLessThanMDAndPreview && this.context.features.message.information;
                //     action.enabled = oneSelected;
                //     break;
            }
        });
    };

    isSelectedDocumentNoteAdded = () => {
        if (this.props.selectedDocuments.length === 1) {
            return getSelectedDocuments(
                this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
                this.props.selectedDocuments
            ).some((document) => !!document.note);
        }

        return false;
    };

    isSelectedDocumentsUnRead = () => {
        if (this.props.previewDocument) {
            return false;
        }

        if (this.props.selectedDocuments.length > 0) {
            return getSelectedDocuments(
                this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
                this.props.selectedDocuments
            ).some((document) => !document.read);
        }

        return false;
    };

    isSelectedDocumentsRead = () => {
        if (this.props.previewDocument) {
            return false;
        }

        if (this.props.selectedDocuments.length > 0) {
            return getSelectedDocuments(
                this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
                this.props.selectedDocuments
            ).some((document) => document.read);
        }

        return false;
    };

    isSelectedDocumentsRemoveFlag = () => {
        if (this.props.previewDocument) {
            return false;
        }

        if (this.props.selectedDocuments.length > 0) {
            return getSelectedDocuments(
                this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
                this.props.selectedDocuments
            ).some((document) => document.flag);
        }

        return false;
    };

    isSelectedDocumentsAddFlag = () => {
        if (this.props.previewDocument) {
            return false;
        }

        if (this.props.selectedDocuments.length > 0) {
            return getSelectedDocuments(
                this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documentList,
                this.props.selectedDocuments
            ).some((document) => !document.flag);
        }

        return false;
    };

    setFolderActionsValueElements = (actions: Array<ISelectionAction>) => {
        actions.forEach((action) => {
            switch (action.selectionValue) {
                case folderActions.MOVE_FOLDER:
                    break;

                case folderActions.RENAME_FOLDER:
                    break;

                case folderActions.DELETE_FOLDER:
                    break;
            }
        });
    };

    handleSelectionAction = (action: string, actionValue: string) => {
        if (this.props.onItemSelected) {
            this.props.onItemSelected(this.props.type, action, actionValue);
        }
    };

    getCustomFolderList = (action: ISelectionAction, folderOffset: number) => {
        let counter = 1000;
        const listElements: JSX.Element[] = [];
        const notTouchScreen = !isTouchScreen();
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        const calcHeight = this.state.customFolders.length * 40;
        const newList = this.state.customFolders.sort(function (a, b) {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        });
        newList.forEach((folder, folderindex) => {
            listElements.push(
                <li
                    id={'folderMoveItemId' + (folderindex + folderOffset)}
                    key={'customListItem' + counter}
                    className={[
                        styles.customFolderListItem,
                        folderindex + folderOffset === this.state.selectedIndex &&
                        !isSmall &&
                        notTouchScreen &&
                        action.enabled
                            ? styles.selectedItem
                            : styles.unSelectedItem,
                    ].join(' ')}
                    onClick={() => this.handleSelectionAction(action.selectionValue, folder.id)}
                    {...(folderindex + folderOffset === this.state.selectedIndex &&
                        action.enabled && { role: 'status' })}
                    {...(notTouchScreen && {
                        onMouseEnter: () => this.onMouseEnterHandler(folderindex + folderOffset),
                        onMouseLeave: this.onMouseLeaveHandler,
                    })}
                >
                    <a
                        className="d-flex align-items-center pl-4 text-truncate"
                        key={'customFolderAction' + counter}
                        tabIndex={-1}
                        href={staticConfig.defaultUrl}
                        onClick={(e) => e.preventDefault()}
                    >
                        <Image
                            id={'customFolderIcon' + counter}
                            src={images.archive}
                            className={[styles.folderIcon, 'flex-shrink-0'].join(' ')}
                            alt="FOLDER_TEXT"
                        />
                        <span className="text-truncate pl-1">{folder.name}</span>
                    </a>
                </li>
            );
            counter++;
        });

        return (
            <ScrollView verticalOnly style={{ height: calcHeight, maxHeight: 150 }}>
                <ul className={['w-100', styles.customFolderList].join(' ')}>{listElements}</ul>
            </ScrollView>
        );
    };

    getDropDownList = () => {
        const listElements: JSX.Element[] = [];
        const actions = this.getListByType();
        this.setElementActionByType(actions);
        const visibleActions = actions.filter((action) => action.visible);
        const notTouchScreen = !isTouchScreen();
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        const [colorDarkGreyAdjusted] = getGlobalCssVariables(['--color-dark-grey-adjusted']);
        const folderCount = this.state.customFolders.length;

        visibleActions.forEach((action, index) => {
            const isSelected =
                this.props.type === MenuBarButtonType.SORT_FILTER &&
                ((action.selectionValue === this.props.sortOrder && action.actionValue === SortFilterType.SORT) ||
                    (action.selectionValue === this.props.filterBy && action.actionValue === SortFilterType.FILTER));
            if (
                this.props.type === MenuBarButtonType.MOVE_TO_FOLDER &&
                action.selectionValue === MoveToFolderItems.CUSTOM_FOLDERS
            ) {
                if (this.state.customFolders.length > 0) {
                    listElements.push(
                        <li key={'item' + index} className="p-0">
                            {this.getCustomFolderList(action, index + 1)}
                        </li>
                    );
                }
            } else {
                listElements.push(
                    action.selectionValue === staticItems.SEPARATOR ? (
                        <li key={'item' + index} className={`w-100' ${styles.separator}`} />
                    ) : (
                        <li
                            tabIndex={0}
                            role={isSelected}
                            aria-selected={isSelected}
                            key={'item' + index}
                            className={[
                                'd-flex align-items-center',
                                isSelected && styles.reduceLeftPadding,
                                !action.enabled && styles.disabled,
                                !action.enabled && 'text-small',
                                folderCount + index === this.state.selectedIndex &&
                                !isSmall &&
                                notTouchScreen &&
                                action.enabled
                                    ? styles.selectedItem
                                    : styles.unSelectedItem,
                            ].join(' ')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    this.handleSelectionAction(action.selectionValue, String(action.actionValue));
                                }
                            }}
                            onClick={(event) => {
                                this.handleSelectionAction(action.selectionValue, String(action.actionValue));
                            }}
                            {...(folderCount + index === this.state.selectedIndex &&
                                action.enabled && { role: 'status' })}
                            {...(notTouchScreen && {
                                onMouseEnter: () => this.onMouseEnterHandler(folderCount + index),
                                onMouseLeave: this.onMouseLeaveHandler,
                            })}
                        >
                            {isSelected && (
                                <div className={['d-flex align-items-center', styles.acceptIconWrap].join(' ')}>
                                    <AcceptIcon className={styles.acceptIcon} stroke={colorDarkGreyAdjusted} />
                                </div>
                            )}
                            <div
                                className={[
                                    'd-inline-block p-0',
                                    isSmall ? 'text-truncate' : 'text-nowrap',
                                    isSelected ? 'bold' : '',
                                ].join(' ')}
                            >
                                <span id="dropdown-text">
                                    {action.translateKey ? (
                                        <FormattedMessage key={'fMsg' + index} id={action.translateKey} />
                                    ) : (
                                        ''
                                    )}{' '}
                                </span>
                                <span id="dropdown-value">{action.valueElements}</span>
                            </div>
                        </li>
                    )
                );
            }
        });

        return (
            <ScrollView className="position-relative">
                <ul>{listElements}</ul>
            </ScrollView>
        );
    };

    public render() {
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        const [colorDarkestBlue] = getGlobalCssVariables(['--color-darkest-blue']);
        return (
            (this.state.showList || (!this.state.showList && isSmall && this.state.showClosingAnim)) && (
                <div
                    ref={this.dropDownListRef}
                    className={[
                        'd-flex',
                        isSmall ? styles.menubarDropDownListOverlay : '',
                        isSmall && this.state.showClosingAnim ? styles.closed : '',
                        isSmall ? 'align-items-center justify-content-center' : '',
                        this.props.rightAligned && this.props.type !== MenuBarButtonType.FOLDER_ACTION
                            ? 'justify-content-end'
                            : 'justify-content-start',
                    ].join(' ')}
                    style={{ backgroundColor: `rgba(${hexToRgb(colorDarkestBlue)}, .4)` }}
                >
                    <div
                        className={cn('d-flex', {
                            [styles.menuButtonList]: true,
                            [styles.dropDownContainer]: true,
                            [styles.mobileDropDownContainerOpened]: isSmall && this.state.showList,
                            [styles.mobileDropDownContainerClosed]: isSmall && this.state.showClosingAnim,
                            [styles.rightAligned]:
                                this.props.rightAligned && this.props.type === MenuBarButtonType.FOLDER_ACTION,
                            [styles.folderActionsContainer]: isSmall && this.state.showList,
                        })}
                    >
                        {this.getDropDownList()}
                    </div>
                </div>
            )
        );
    }
}

const mapStateToProps = ({ mailbox, documentPreview, appReadouts, menubar, sender }: IRootState) => {
    return {
        folders: mailbox.folderList,
        selectedFolder: mailbox.selectedFolder,
        selectedDocuments: mailbox.selectedDocuments,
        documentList:
            mailbox.selectedSender == null
                ? mailbox.documentList.slice(0, mailbox.currentShownDocumentsCount)
                : mailbox.filteredDocumentList.slice(0, mailbox.currentShownDocumentsCount),
        previewDocument: documentPreview.document,
        activeArifact: documentPreview.activeArtifact,
        sortOrder: mailbox.sortOrder,
        filterBy: mailbox.filterBy,
        mediaSize: appReadouts.mediaSize,
        orientation: appReadouts.orientation,
        readUnreadButtonDisabled: menubar.disableReadUnreadButton,
        addRemoveFlagButtonDisabled: menubar.disableAddRemoveFlagButton,
        access: mailbox.mailbox.access,
        isSenderClicked: sender.isSenderClicked,
        selectSenderFolder: mailbox.selectSenderFolder,
    };
};

type ReduxType = IMenuBarDropdownListProps & ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(MenuBarDropdownList);
